import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import {
  type BillingPrice,
  type BillingProduct,
} from '@tribeplatform/gql-client/types'
import { useDomain } from '@tribeplatform/react-components/common/hooks'
import { useI18n } from '@tribeplatform/react-components/i18n'
import {
  useCreateSubscriptionCheckoutSession,
  useUpdateSubscriptionInterval,
  useUpdateSubscriptionPlan,
} from '@tribeplatform/react-sdk/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useTrackCheckoutStarted } from '../hooks/useTrackCheckoutStarted.js'
import { CheckoutStartedQueryParams } from '../types.js'

interface Props {
  currentProductOrder: number
  newProduct: BillingProduct
  price: BillingPrice
}

export const usePlanCardButtonActions = ({
  currentProductOrder,
  newProduct,
  price,
}: Props) => {
  const { $t } = useI18n()
  const { trackCheckoutStarted } = useTrackCheckoutStarted()
  const { mutateAsync: createSession, isLoading: isCreatingSession } =
    useCreateSubscriptionCheckoutSession()
  const {
    mutateAsync: updateSubscriptionPlan,
    isLoading: isUpdatingSubscriptionPlan,
  } = useUpdateSubscriptionPlan()
  const {
    mutateAsync: updateSubscriptionInterval,
    isLoading: isUpdatingInterval,
  } = useUpdateSubscriptionInterval()
  const { getResourceUrl } = useDomain()

  const isLoading =
    isCreatingSession || isUpdatingSubscriptionPlan || isUpdatingInterval

  const showPlanUpdatedToast = () => {
    toast({
      title: $t({
        defaultMessage: 'Plan updated',
        id: 'Admin.Billing.ChangePlan.Success',
      }),
      description: $t({
        defaultMessage:
          'Your plan is updated. Changes may take a few moments to appear in your account.',
        id: 'Admin.Billing.ChangePlan.SuccessDescription',
      }),
      status: 'success',
    })
  }

  const showChangePlanErrorToast = () => {
    toast({
      title: $t({
        defaultMessage: 'Something went wrong',
        id: 'Admin.Billing.ChangePlan.Error',
      }),
      description: $t({
        defaultMessage: 'Unable to change plan',
        id: 'Admin.Billing.ChangePlan.ErrorDescription',
      }),
      status: 'error',
    })
  }

  const switchBillingInterval = async (options?: { sendToast?: boolean }) => {
    const { sendToast = true } = options ?? {}

    try {
      await updateSubscriptionInterval({
        interval: price.interval,
      })

      if (sendToast) {
        showPlanUpdatedToast()
      }
    } catch {
      showChangePlanErrorToast()
    }
  }

  const openCheckout = async () => {
    const newWindow = window.open()
    const planId = newProduct.id
    const planName = newProduct.name
    const planAmount = price.amount
    const planInterval = price.interval

    const checkoutQs: CheckoutStartedQueryParams = {
      planId,
      planName,
      planAmount: `${planAmount}`,
      planInterval,
      success: 'true',
    }

    const queryParams = new URLSearchParams({ ...checkoutQs })

    const successUrl = getResourceUrl(
      `${adminRoutes.billing.root}?sessionId={CHECKOUT_SESSION_ID}&${queryParams.toString()}`,
    )
    const cancelUrl = getResourceUrl(
      `${adminRoutes.billing.plans}?success=false&sessionId={CHECKOUT_SESSION_ID}`,
    )

    await trackCheckoutStarted({
      planId,
      planName,
      planAmount,
      planInterval,
    })

    try {
      const session = await createSession({
        priceId: price.id,
        successUrl,
        cancelUrl,
      })
      newWindow.location.href = session.url
    } catch {
      newWindow.close()
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Admin.Billing.BillingPortal.Error',
        }),
        description: $t({
          defaultMessage: 'Unable to open billing portal',
          id: 'Admin.Billing.BillingPortal.ErrorDescription',
        }),
        status: 'error',
      })
    }
  }

  const autoUpdatePlan = async () => {
    const newWindow = window.open()

    try {
      await updateSubscriptionPlan({
        priceId: price.id,
      })

      const queryParams = new URLSearchParams()

      if (newProduct.order > currentProductOrder) {
        queryParams.append('upgrade', 'true')
      }

      newWindow.location.href = `${adminRoutes.billing.root}?${queryParams.toString()}`
      showPlanUpdatedToast()
    } catch {
      newWindow.close()
      showChangePlanErrorToast()
    }
  }

  return {
    autoUpdatePlan,
    isLoading,
    openCheckout,
    switchBillingInterval,
  }
}
